// We only run on ElevenLabs' EU residency. Their status page has no per-region components,
// so the affected region is only recognisable from the wording of an incident. Anything
// that explicitly includes every region counts as well.
const EU_SCOPE_PATTERN = /\b(eu|europ\w*|worldwide|all regions|all residencies)\b/i;

const CLOSED_STATUSES = new Set(["resolved", "postmortem"]);

const asRecord = (value: unknown): Record<string, unknown> | null =>
    value && typeof value === "object" && !Array.isArray(value)
        ? value as Record<string, unknown>
        : null;

const asString = (value: unknown): string => typeof value === "string" ? value : "";

const recordsOf = (items: unknown[]): Record<string, unknown>[] =>
    items.flatMap(item => {
        const record = asRecord(item);
        return record ? [record] : [];
    });

// A payload without its incident list is an unknown format, not an all-clear. Throwing
// lets the caller treat the source as failed, so it cannot switch off an active banner.
const requireIncidentList = (payload: unknown, key: string): Record<string, unknown>[] => {
    const list = asRecord(payload)?.[key];
    if (!Array.isArray(list)) {
        throw new Error(`ElevenLabs status payload has no "${key}" list`);
    }
    return recordsOf(list);
};

// Unknown statuses count as open: missing a real outage is worse than a late banner removal.
const isOpen = (incident: Record<string, unknown>): boolean =>
    !CLOSED_STATUSES.has(asString(incident.status));

export const mentionsEuScope = (text: string): boolean => EU_SCOPE_PATTERN.test(text);

// incident.io widget API (/api/v1/summary): lists only ongoing incidents with their latest update.
export const hasOngoingEuIncident = (payload: unknown): boolean =>
    requireIncidentList(payload, "ongoing_incidents").some(incident =>
        isOpen(incident)
        && mentionsEuScope(`${asString(incident.name)}\n${asString(incident.last_update_message)}`));

// Statuspage-compatible API (/api/v2/incidents.json): recent incidents with every update.
export const hasOpenEuIncidentInHistory = (payload: unknown): boolean =>
    requireIncidentList(payload, "incidents").some(incident => {
        if (!isOpen(incident)) return false;
        const updates = Array.isArray(incident.incident_updates) ? incident.incident_updates : [];
        const updateBodies = recordsOf(updates).map(update => asString(update.body));
        return mentionsEuScope([asString(incident.name), ...updateBodies].join("\n"));
    });
